import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Map, Source, Layer, Popup, ScaleControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { withStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Button,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PropTypes from 'prop-types';

import { ColorUtil } from 'utils';
import { TaxonomySelect } from 'components';
import { LocationsTabs } from './LocationsTabs';
import axiosInstance from 'utils/axiosUtil';

const mapboxToken =
  'pk.eyJ1IjoiZG9jdGl2aXR5IiwiYSI6ImNsMmo2OWx6bTBtdmUza3J6eDQ2Z3pvdG0ifQ.o9j6xJxOa2WKQLYdqQaJRQ';

// Fallback center when there is no requested location and no saved last-position:
// Doctivity's office in Bloomsburg, PA.
const BLOOMSBURG_OFFICE = { lat: 41.0011, lng: -76.4257 };
const DEFAULT_ZOOM = 12;
const LAST_POSITION_KEY = 'locationsMap.lastPosition';
// How long to wait after the map stops moving before hitting the DB (ms).
const FETCH_DEBOUNCE_MS = 400;
// Overall ceiling on how many locations we'll load for one view...
const MAX_LOCATIONS = 2000;
// ...paged in this many at a time so a zoomed-out area fills in progressively instead of stalling.
const BATCH_SIZE = 100;
// Below this zoom the area covers too many locations to be useful/fast — we show nothing and ask the
// user to zoom in. (Higher zoom = more zoomed in; zooming out lowers the number past this floor.)
const MIN_ZOOM = 10;
// Keep clustering active all the way to the max zoom so a pile of coincident locations (same
// building/address) ALWAYS renders as a counted cluster and never collapses into overlapping dots
// that look like a single pin. Spread-out pins still separate as you zoom (clusterRadius is pixels).
const CLUSTER_MAX_ZOOM = 22;
// Two pins count as "the same spot" (a stack that can never be split by zooming) when their lat/lng
// differ by less than this. ~1e-5 degrees ≈ 1 meter, i.e. records geocoded to the same point. A
// cluster where all leaves are within this opens the sidebar; anything more spread out zooms to split.
const COINCIDENT_EPS = 1e-5;

// Affiliation colouring: blue = the location has >=1 provider affiliated to the selected client,
// red = none. Uses the app-wide standard affiliated/non-affiliated colours (ColorUtil), same as the
// Projected Patients / chart chips elsewhere.
const COLOR_AFFILIATED = ColorUtil.AffiliatedColor;
const COLOR_UNAFFILIATED = ColorUtil.NonAffiliatedColor;

// Per-browser "last place you were" persistence. Wrapped in try/catch: localStorage can throw or
// be unavailable (private windows, blocked site data), and we must render fine without it.
const readLastPosition = () => {
  try {
    const raw = window.localStorage.getItem(LAST_POSITION_KEY);
    if (raw) {
      const pos = JSON.parse(raw);
      if (typeof pos?.lat === 'number' && typeof pos?.lng === 'number') {
        return pos;
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const writeLastPosition = (pos) => {
  try {
    window.localStorage.setItem(LAST_POSITION_KEY, JSON.stringify(pos));
  } catch (e) {
    // ignore
  }
};

const EMPTY_COUNTS = {
  provider_count: 0,
  contact_count: 0,
  notes_count: 0,
  organization_id: null,
  organization_name: null,
  captured_claims_count: 0,
};

// Sidebar ranking: most providers first, then most contacts as a tiebreak.
const rankByRelevance = (rows) =>
  [...rows].sort(
    (a, b) =>
      (b.provider_count || 0) - (a.provider_count || 0) ||
      (b.contact_count || 0) - (a.contact_count || 0)
  );

const formatAddressParts = (loc) =>
  [loc.address1, loc.city, loc.state, loc.postal_code].filter(Boolean).join(', ');

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
    height: 'calc(100vh - 200px)',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    overflow: 'visible',
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 300,
  },
  searchField: {
    flex: 1,
    maxWidth: 500,
  },
  mapContainer: {
    position: 'relative',
    flex: 1,
    minHeight: 400,
    borderRadius: 4,
    overflow: 'visible',
    '& .mapboxgl-popup': {
      zIndex: '1000 !important',
    },
    '& .mapboxgl-popup-content': {
      maxHeight: '400px !important',
      overflowY: 'auto !important',
      padding: '12px 14px !important',
    },
  },
  centerButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 5,
    backgroundColor: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
    '&:hover': { backgroundColor: '#f5f5f5' },
  },
  zoomMessage: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'rgba(255, 255, 255, 0.92)',
    padding: theme.spacing(2, 3),
    borderRadius: 8,
    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.text.primary,
    zIndex: 5,
    pointerEvents: 'none',
    textAlign: 'center',
  },
  popupName: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 2,
  },
  popupAddress: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  popupHint: {
    fontSize: 11,
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  popupLinks: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: 8,
    gap: 2,
  },
  popupLink: {
    textTransform: 'none',
    padding: '2px 0',
    minWidth: 0,
    justifyContent: 'flex-start',
    textAlign: 'left',
    // Let a long "Organization: ..." wrap flush-left instead of hanging-indenting under the label.
    whiteSpace: 'normal',
    lineHeight: 1.3,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  affiliatedLegend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  dotAffiliated: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: ColorUtil.AffiliatedColor,
    display: 'inline-block',
  },
  dotUnaffiliated: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: ColorUtil.NonAffiliatedColor,
    display: 'inline-block',
    marginLeft: theme.spacing(1),
  },
  sidebarPaper: {
    width: 400,
    maxWidth: '90vw',
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sidebarLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  sidebarEmpty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  sidebarRow: {
    // Zebra striping: alternating white / light-gray rows.
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  rowPrimary: {
    fontWeight: 500,
  },
  // The location currently shown in the popup gets highlighted in the list: standard left-menu green
  // (#00A887) at 20% opacity. Overrides the zebra striping for that row.
  primaryRow: {
    backgroundColor: 'rgba(0, 168, 135, 0.2)',
    '&:nth-of-type(even)': {
      backgroundColor: 'rgba(0, 168, 135, 0.2)',
    },
  },
  rowBadges: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
});

// Compute a radius (miles) that encloses the visible viewport corners, so pins fill the screen.
const radiusFromMap = (map) => {
  const center = map.getCenter();
  const bounds = map.getBounds();
  const ne = bounds.getNorthEast();
  const latMiles = Math.abs(ne.lat - center.lat) * 69; // center -> top edge
  const lngMiles = Math.abs(ne.lng - center.lng) * 69 * Math.cos((center.lat * Math.PI) / 180);
  // Corner distance (half-diagonal) covers the whole viewport, not just the mid-edges.
  return Math.max(1, Math.round(Math.sqrt(latMiles * latMiles + lngMiles * lngMiles)));
};

function LocationsMapPage({ classes }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.user);
  // Selected client drives the affiliation lens (which locations are "ours"). null = no lens.
  const selectedClient = useSelector((state) => state.app?.selectedClient);

  const mapRef = useRef(null);
  const fetchTimerRef = useRef(null);
  // Monotonic request id — only the latest area load is applied (drops stale/superseded pans).
  const fetchSeqRef = useRef(0);
  // Latest sidebar-load id — a newer cluster click abandons an older still-loading one.
  const sidebarSeqRef = useRef(0);
  // Client-side counts cache keyed by location id. undefined = never fetched, null = in-flight,
  // object = loaded.
  const countsCacheRef = useRef({});

  const [searchAddress, setSearchAddress] = useState(() => searchParams.get('address') || '');
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setCountsVersion] = useState(0); // bump to re-render when a popup's counts arrive
  const [selectedInfo, setSelectedInfo] = useState(null); // clicked-pin popup
  const [cursor, setCursor] = useState('grab');
  const [belowMinZoom, setBelowMinZoom] = useState(false);
  // `anchor` is the [lng, lat] the popup sits at for this sidebar session (the clicked pin/cluster).
  // Selecting a sidebar row swaps the popup's contents but keeps it anchored here (the listed
  // locations are same-spot / same-building, so one anchor is right for all of them).
  const [sidebar, setSidebar] = useState({ open: false, loading: false, rows: [], anchor: null });
  const [contextMenu, setContextMenu] = useState(null); // { mouseX, mouseY, location }

  const [centerLocation, setCenterLocation] = useState(() => {
    const locationId = searchParams.get('locationId');
    return locationId ? { id: parseInt(locationId, 10) } : null;
  });

  const [initialView] = useState(() => {
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { latitude: lat, longitude: lng, zoom: DEFAULT_ZOOM };
    }
    const last = readLastPosition();
    if (last) {
      return { latitude: last.lat, longitude: last.lng, zoom: last.zoom || DEFAULT_ZOOM };
    }
    return { latitude: BLOOMSBURG_OFFICE.lat, longitude: BLOOMSBURG_OFFICE.lng, zoom: DEFAULT_ZOOM };
  });

  // "Home" for the recenter button: the requested location (URL) if any, else the Bloomsburg office.
  const [homeCenter] = useState(() => {
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    return BLOOMSBURG_OFFICE;
  });

  // Specialty filter: selected taxonomy objects from the standard SpecialtySearch picker (via
  // TaxonomySelect). Empty = all. We send their .code values to the backend.
  const [specialtyValues, setSpecialtyValues] = useState([]);
  // Bumped on every specialty pick so the reload effect fires even when the picker hands us the
  // same array reference (SpecialtySearch re-sends its current selection on Confirm, which would
  // otherwise be a no-op setState and skip the refetch).
  const [specialtyNonce, setSpecialtyNonce] = useState(0);
  // Affiliated-only filter (blue = affiliated colouring is always on).
  const [affiliatedOnly, setAffiliatedOnly] = useState(false);

  // Fetch provider/contact/org counts (+ org captured_claims_count) for a set of ids, using and
  // filling the cache. Returns [{ id, ...counts }] for every requested id.
  const loadCounts = useCallback(async (ids) => {
    const need = ids.filter((id) => {
      const v = countsCacheRef.current[id];
      return v === undefined || v === null;
    });
    if (need.length) {
      try {
        const resp = await axiosInstance.get(`Locations/location-counts?ids=${need.join(',')}`);
        const data = resp.data || {};
        need.forEach((id) => {
          countsCacheRef.current[id] = data[id] || { ...EMPTY_COUNTS };
        });
      } catch (error) {
        console.error('Error fetching location counts:', error);
        need.forEach((id) => {
          if (countsCacheRef.current[id] === null) delete countsCacheRef.current[id];
        });
      }
    }
    return ids.map((id) => ({ id, ...(countsCacheRef.current[id] || EMPTY_COUNTS) }));
  }, []);

  // Counts for one location, fetched lazily when its pin is clicked (they show in the popup).
  const fetchCountsForId = useCallback(
    (id) => {
      if (countsCacheRef.current[id] !== undefined) return; // cached or in-flight
      loadCounts([id]).then(() => setCountsVersion((v) => v + 1));
    },
    [loadCounts]
  );

  // Show the popup for a location and set it as the popup's content. The popup is the single place
  // that links out to other pages; the sidebar just picks which location the popup shows.
  const showPopupFor = useCallback(
    (id, loc, anchor) => {
      if (!id || !anchor) return;
      setSelectedInfo({
        longitude: anchor[0],
        latitude: anchor[1],
        properties: {
          id,
          name: loc?.name || '',
          address: loc?.address || '',
          organization_id: loc?.organization_id || 0,
        },
      });
      fetchCountsForId(id);
    },
    [fetchCountsForId]
  );

  // Open the sidebar for one location: the clicked ("representative") location pinned at the top,
  // followed by its address-similar neighbours ranked by relevance. Sidebar match = the location
  // detail page's SimilarLocationsTable (same city + state + address1 prefix). We also open the popup
  // for the representative right away (the popup is always shown alongside the sidebar). `anchor` is
  // the [lng, lat] the popup sits at; selecting a row keeps that anchor and just swaps the contents.
  const openSidebarForLocation = useCallback(
    (repId, repProps, anchor) => {
      if (!repId || !anchor) {
        sidebarSeqRef.current += 1;
        setSidebar({ open: true, loading: false, rows: [], anchor: null });
        return;
      }
      showPopupFor(repId, repProps, anchor);
      const seq = sidebarSeqRef.current + 1;
      sidebarSeqRef.current = seq;
      setSidebar({ open: true, loading: true, rows: [], anchor });
      (async () => {
        try {
          const resp = await axiosInstance.get(`Locations/similar?locationId=${repId}`);
          if (seq !== sidebarSeqRef.current) return;
          const simRows = resp.data || [];
          const counts = await loadCounts([repId, ...simRows.map((r) => r.id)]);
          if (seq !== sidebarSeqRef.current) return;
          const byId = {};
          counts.forEach((c) => {
            byId[c.id] = c;
          });
          const baseRow = {
            id: repId,
            name: repProps?.name || null,
            address: repProps?.address || '',
            ...byId[repId],
          };
          const similarRows = simRows.map((r) => ({
            id: r.id,
            name: r.name || null,
            address: formatAddressParts(r),
            organization_id: r.organization_id || null,
            ...byId[r.id],
          }));
          // Keep the clicked location pinned first; rank only its neighbours below it.
          const rows = [baseRow, ...rankByRelevance(similarRows)];
          setSidebar({ open: true, loading: false, rows, anchor });
        } catch (error) {
          console.error('Error fetching similar locations:', error);
          if (seq === sidebarSeqRef.current) {
            setSidebar({ open: true, loading: false, rows: [], anchor });
          }
        }
      })();
    },
    [loadCounts, showPopupFor]
  );

  // Coincident cluster → open the sidebar for any one of its (identical-coordinate) leaves, anchored
  // at that leaf's coordinates.
  const openSidebarForCluster = useCallback(
    (clusterId) => {
      const map = mapRef.current;
      if (!map) return;
      const source = map.getSource('locations');
      sidebarSeqRef.current += 1;
      setSidebar({ open: true, loading: true, rows: [], anchor: null });
      source.getClusterLeaves(clusterId, 1, 0, (err, leaves) => {
        const rep = !err && leaves && leaves[0] ? leaves[0] : null;
        openSidebarForLocation(
          rep ? rep.properties.id : null,
          rep ? rep.properties : null,
          rep ? rep.geometry.coordinates : null
        );
      });
    },
    [openSidebarForLocation]
  );

  const loadArea = useCallback(
    async (lat, lng, radiusMiles) => {
      const seq = fetchSeqRef.current + 1;
      fetchSeqRef.current = seq;
      setLoading(true);
      const excludeParam = centerLocation?.id ? `&excludeLocationId=${centerLocation.id}` : '';
      const codes = specialtyValues
        .map((v) => (typeof v === 'string' ? v : v.code))
        .filter(Boolean);
      const specialtyParam = codes.length
        ? `&taxonomyCodes=${codes.map(encodeURIComponent).join(',')}`
        : '';
      const clientParam = selectedClient ? `&selected_client_id=${selectedClient}` : '';
      const affiliatedParam = affiliatedOnly ? '&affiliatedOnly=1' : '';
      const all = [];
      try {
        for (let offset = 0; offset < MAX_LOCATIONS; offset += BATCH_SIZE) {
          const url = `Locations/nearby-locations?lat=${lat}&lng=${lng}&radius=${radiusMiles}&limit=${BATCH_SIZE}&offset=${offset}${excludeParam}${specialtyParam}${clientParam}${affiliatedParam}`;
          // Sequential on purpose: each page appends and paints before the next is requested.
          // eslint-disable-next-line no-await-in-loop
          const response = await axiosInstance.get(url);
          if (seq !== fetchSeqRef.current) return; // superseded by a newer pan
          const batch = response.data || [];
          all.push(...batch);
          setNearbyLocations(all.slice());
          if (batch.length < BATCH_SIZE) break; // last page
        }
      } catch (error) {
        if (seq !== fetchSeqRef.current) return;
        console.error('Error fetching nearby locations:', error);
        setNearbyLocations([]);
      } finally {
        if (seq === fetchSeqRef.current) setLoading(false);
      }
    },
    [centerLocation, specialtyValues, selectedClient, affiliatedOnly]
  );

  const handleMapMoveEnd = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const center = map.getCenter();
    const zoom = map.getZoom();

    writeLastPosition({ lat: center.lat, lng: center.lng, zoom });
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);

    if (zoom < MIN_ZOOM) {
      fetchSeqRef.current += 1;
      setNearbyLocations([]);
      setBelowMinZoom(true);
      setLoading(false);
      return;
    }

    setBelowMinZoom(false);
    const radiusMiles = radiusFromMap(map);
    fetchTimerRef.current = setTimeout(() => {
      loadArea(center.lat, center.lng, radiusMiles);
    }, FETCH_DEBOUNCE_MS);
  }, [loadArea]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    const locationId = searchParams.get('locationId');
    setCenterLocation(locationId ? { id: parseInt(locationId, 10) } : null);
    if (!Number.isNaN(lat) && !Number.isNaN(lng) && mapRef.current) {
      mapRef.current.flyTo({ center: [lng, lat], zoom: DEFAULT_ZOOM, duration: 1000 });
    }
  }, [searchParams]);

  useEffect(
    () => () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    },
    []
  );

  // Re-run the current area load when a filter changes. The `!mapRef.current` guard skips the
  // initial mount (the map's onLoad drives the first load); afterwards a change refetches.
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    if (map.getZoom() < MIN_ZOOM) return;
    const center = map.getCenter();
    loadArea(center.lat, center.lng, radiusFromMap(map));
  }, [specialtyNonce, affiliatedOnly, selectedClient]);

  const handleSearch = useCallback(async () => {
    if (!searchAddress.trim()) return;
    setLoading(true);
    try {
      const encodedAddress = encodeURIComponent(searchAddress);
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxToken}&limit=1`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        setCenterLocation(null);
        if (mapRef.current) {
          mapRef.current.flyTo({ center: [lng, lat], zoom: DEFAULT_ZOOM, duration: 1000 });
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      setLoading(false);
    }
  }, [searchAddress]);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Recenter on home, keeping the current zoom (easeTo defaults to the current zoom).
  const handleRecenter = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.easeTo({ center: [homeCenter.lng, homeCenter.lat], duration: 800 });
  }, [homeCenter]);

  const handleMapClick = useCallback(
    (event) => {
      if (!mapRef.current) return;
      const features = mapRef.current.queryRenderedFeatures(event.point, {
        layers: ['clusters', 'unclustered-point'],
      });
      if (!features.length) {
        setSelectedInfo(null);
        return;
      }
      const feature = features[0];
      if (feature.layer.id === 'clusters') {
        const clusterId = feature.properties.cluster_id;
        const source = mapRef.current.getSource('locations');
        const currentZoom = mapRef.current.getZoom();
        const pointCount = feature.properties.point_count || 0;
        // Decide zoom-vs-sidebar by whether the pins are actually stacked on ONE spot, not by a zoom
        // threshold (Mapbox's expansion zoom rounds/caps near the max and can't be trusted for this).
        // Pull the cluster's leaves: if they span more than one coordinate, zooming will separate them
        // into sub-clusters, so zoom in (e.g. a "6" that becomes "3" + "3"). Only a true single-point
        // stack — every leaf on the same coordinate, which can never split — opens the sidebar.
        source.getClusterLeaves(clusterId, pointCount || 1000, 0, (leafErr, leaves) => {
          const c0 = !leafErr && leaves && leaves[0] ? leaves[0].geometry.coordinates : null;
          const coincident =
            c0 &&
            leaves.every((l) => {
              const c = l.geometry.coordinates;
              return (
                Math.abs(c[0] - c0[0]) < COINCIDENT_EPS && Math.abs(c[1] - c0[1]) < COINCIDENT_EPS
              );
            });
          if (coincident) {
            openSidebarForCluster(clusterId);
            return;
          }
          source.getClusterExpansionZoom(clusterId, (zErr, expZoom) => {
            const target =
              !zErr && expZoom > currentZoom
                ? Math.min(expZoom, CLUSTER_MAX_ZOOM)
                : Math.min(currentZoom + 2, CLUSTER_MAX_ZOOM);
            mapRef.current.easeTo({ center: feature.geometry.coordinates, zoom: target });
          });
        });
      } else {
        // A single (unclustered) pin: open the sidebar for it, which also shows the popup.
        openSidebarForLocation(
          feature.properties.id,
          feature.properties,
          feature.geometry.coordinates
        );
      }
    },
    [openSidebarForCluster, openSidebarForLocation]
  );

  const handleContextMenu = useCallback((event, location) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, location });
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // The context menu overlay is click-through (see the Menu's pointerEvents:none root below) so a
  // right-click on another pin/row can retarget it without a left-click to dismiss first. That means
  // the modal backdrop no longer closes it, so we close it ourselves on any outside press or Escape.
  useEffect(() => {
    if (contextMenu === null) return undefined;
    const onDown = (e) => {
      if (!e.target.closest?.('.MuiPopover-paper')) setContextMenu(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setContextMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  const openInNewTab = useCallback((path) => {
    window.open(path, '_blank', 'noopener,noreferrer');
    setContextMenu(null);
  }, []);

  const closeSidebar = useCallback(() => {
    sidebarSeqRef.current += 1; // abandon any in-flight load
    setSidebar((s) => ({ ...s, open: false }));
  }, []);

  const geojson = {
    type: 'FeatureCollection',
    features: nearbyLocations
      .filter(
        (loc) => !Number.isNaN(parseFloat(loc.latitude)) && !Number.isNaN(parseFloat(loc.longitude))
      )
      .map((location) => ({
        type: 'Feature',
        properties: {
          id: location.id,
          name: location.name || '',
          address: formatAddressParts(location),
          organization_id: location.organization_id || 0,
          affiliated: location.affiliated ? 1 : 0,
        },
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(location.longitude), parseFloat(location.latitude)],
        },
      })),
  };

  // Admin gate.
  if (!user?.is_admin) {
    return (
      <>
        <LocationsTabs tab='locations_map' />
        <div className={classes.container}>
          <Card>
            <CardContent>
              <Typography>Access denied. Admin privileges required.</Typography>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const selectedCounts = selectedInfo
    ? countsCacheRef.current[selectedInfo.properties.id] || {}
    : {};
  const selectedOrgId =
    selectedInfo && parseInt(selectedInfo.properties.organization_id, 10)
      ? parseInt(selectedInfo.properties.organization_id, 10)
      : null;

  const renderSidebarRow = (row) => {
    const isSelected = selectedInfo && selectedInfo.properties.id === row.id;
    return (
      <ListItem
        key={row.id}
        disablePadding
        className={`${classes.sidebarRow}${isSelected ? ` ${classes.primaryRow}` : ''}`}
      >
        <ListItemButton
          onClick={() => showPopupFor(row.id, row, sidebar.anchor)}
          onContextMenu={(e) => handleContextMenu(e, row)}
        >
          <ListItemText
            primaryTypographyProps={{ className: classes.rowPrimary }}
            secondaryTypographyProps={{ component: 'div' }}
            primary={row.name || row.organization_name || 'Location'}
            secondary={
              <>
                <div>{row.address}</div>
                <div className={classes.rowBadges}>
                  {`${row.provider_count || 0} providers · ${row.contact_count || 0} contacts · ${row.notes_count || 0} activity notes`}
                </div>
              </>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <>
      <LocationsTabs tab='locations_map' />
      <div className={classes.container}>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <Box className={classes.controls}>
              <div className={classes.searchContainer}>
                <TextField
                  className={classes.searchField}
                  placeholder='Search address...'
                  variant='outlined'
                  size='small'
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton onClick={handleSearch} edge='end' size='small'>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <TaxonomySelect
                values={specialtyValues}
                onChange={(v) => {
                  setSpecialtyValues(Array.isArray(v) ? v : []);
                  setSpecialtyNonce((n) => n + 1);
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={affiliatedOnly}
                    onChange={(e) => setAffiliatedOnly(e.target.checked)}
                  />
                }
                label='Affiliated only'
              />
              <span className={classes.affiliatedLegend}>
                <span className={classes.dotAffiliated} />
                Affiliated
                <span className={classes.dotUnaffiliated} />
                No affiliation
              </span>
              {loading && (
                <div className={classes.loading}>
                  <CircularProgress size={16} />
                  <span>Loading...</span>
                </div>
              )}
            </Box>

            <div className={classes.mapContainer}>
              <Map
                mapLib={import('mapbox-gl')}
                ref={mapRef}
                initialViewState={initialView}
                style={{ width: '100%', height: '100%' }}
                mapStyle='mapbox://styles/mapbox/light-v10'
                mapboxAccessToken={mapboxToken}
                attributionControl={false}
                cursor={cursor}
                onLoad={handleMapMoveEnd}
                onMoveEnd={handleMapMoveEnd}
                onClick={handleMapClick}
                onMouseEnter={() => setCursor('pointer')}
                onMouseLeave={() => setCursor('grab')}
                interactiveLayerIds={['clusters', 'unclustered-point']}
              >
                {/* Distance scale for the current zoom, always visible */}
                <ScaleControl unit='imperial' position='bottom-left' />

                {geojson.features.length > 0 && (
                  <Source
                    id='locations'
                    type='geojson'
                    data={geojson}
                    cluster
                    clusterMaxZoom={CLUSTER_MAX_ZOOM}
                    clusterRadius={50}
                    clusterProperties={{ hasAffiliated: ['max', ['get', 'affiliated']] }}
                  >
                    <Layer
                      id='clusters'
                      type='circle'
                      filter={['has', 'point_count']}
                      paint={{
                        // Blue if the cluster has any affiliated location, red if none.
                        'circle-color': ['case', ['>', ['get', 'hasAffiliated'], 0], COLOR_AFFILIATED, COLOR_UNAFFILIATED],
                        'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 25, 30],
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#fff',
                      }}
                    />
                    <Layer
                      id='cluster-count'
                      type='symbol'
                      filter={['has', 'point_count']}
                      layout={{
                        'text-field': '{point_count_abbreviated}',
                        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                        'text-size': 12,
                      }}
                      paint={{ 'text-color': '#ffffff' }}
                    />
                    <Layer
                      id='unclustered-point'
                      type='circle'
                      filter={['!', ['has', 'point_count']]}
                      paint={{
                        // Blue if affiliated to the selected client, red if not.
                        'circle-color': ['case', ['==', ['get', 'affiliated'], 1], COLOR_AFFILIATED, COLOR_UNAFFILIATED],
                        'circle-radius': 8,
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#fff',
                      }}
                    />
                  </Source>
                )}

                {selectedInfo && (
                  <Popup
                    longitude={selectedInfo.longitude}
                    latitude={selectedInfo.latitude}
                    anchor='bottom'
                    closeButton
                    closeOnClick={false}
                    offset={15}
                    maxWidth='560px'
                    onClose={() => setSelectedInfo(null)}
                  >
                    <div
                      onContextMenu={(e) =>
                        handleContextMenu(e, {
                          id: selectedInfo.properties.id,
                          organization_id: selectedOrgId,
                          organization_name: selectedCounts.organization_name,
                        })}
                    >
                      <div className={classes.popupName}>
                        {selectedInfo.properties.name || selectedCounts.organization_name || 'Location'}
                      </div>
                      <div className={classes.popupAddress}>{selectedInfo.properties.address}</div>
                      <div className={classes.popupHint}>Right-click for actions in a new tab</div>
                      <Divider sx={{ my: 1 }} />
                      <div className={classes.popupLinks}>
                        <Button
                          className={classes.popupLink}
                          size='small'
                          onClick={() => navigate(`/locations/${selectedInfo.properties.id}`)}
                        >
                          Location details
                        </Button>
                        <Divider sx={{ my: 0.5, alignSelf: 'stretch' }} />
                        {selectedOrgId && (
                          <Button
                            className={classes.popupLink}
                            size='small'
                            onClick={() => navigate(`/organizations/${selectedOrgId}`)}
                          >
                            {selectedCounts.organization_name
                              ? `Organization: ${selectedCounts.organization_name}`
                              : 'Organization'}
                          </Button>
                        )}
                        <Button
                          className={classes.popupLink}
                          size='small'
                          onClick={() =>
                            navigate(`/locations/${selectedInfo.properties.id}/providers`)}
                        >
                          Providers
                          {selectedCounts.provider_count != null
                            ? ` (${selectedCounts.provider_count})`
                            : ''}
                        </Button>
                        <Button
                          className={classes.popupLink}
                          size='small'
                          onClick={() =>
                            navigate(`/locations/${selectedInfo.properties.id}/contacts`)}
                        >
                          Contacts
                          {selectedCounts.contact_count != null
                            ? ` (${selectedCounts.contact_count})`
                            : ''}
                        </Button>
                      </div>
                    </div>
                  </Popup>
                )}
              </Map>

              <Tooltip title='Re-center (keep zoom)'>
                <IconButton className={classes.centerButton} onClick={handleRecenter} size='small'>
                  <MyLocationIcon fontSize='small' />
                </IconButton>
              </Tooltip>

              {belowMinZoom && <div className={classes.zoomMessage}>Zoom in to see locations</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Similar/grouped-locations sidebar. Persistent (not temporary) so it has no dimming modal
          backdrop — the map stays interactive and a pin popup can show at the same time. Closed with
          the ✕ button (or when a new selection replaces it). */}
      <Drawer
        anchor='right'
        variant='persistent'
        open={sidebar.open}
        classes={{ paper: classes.sidebarPaper }}
      >
        <div className={classes.sidebarHeader}>
          <Typography variant='h6'>Locations here ({sidebar.rows.length})</Typography>
          <IconButton onClick={closeSidebar} size='small'>
            <CloseIcon />
          </IconButton>
        </div>

        {sidebar.loading && (
          <div className={classes.sidebarLoading}>
            <CircularProgress size={16} />
            <Typography variant='body2'>Loading...</Typography>
          </div>
        )}

        {!sidebar.loading && sidebar.rows.length === 0 && (
          <Typography variant='body2' className={classes.sidebarEmpty}>
            No locations found.
          </Typography>
        )}

        <List dense>{sidebar.rows.map(renderSidebarRow)}</List>
      </Drawer>

      {/* Right-click actions (popup + sidebar) — open in a new tab so the map stays put */}
      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference='anchorPosition'
        anchorPosition={
          contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
        hideBackdrop
        disableScrollLock
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          // Let clicks/right-clicks pass through the overlay to the pins/rows underneath so the menu
          // can be re-triggered on a new target; keep the menu itself interactive.
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto' } },
        }}
      >
        <MenuItem onClick={() => openInNewTab(`/locations/${contextMenu?.location?.id}`)}>
          Open location
        </MenuItem>
        {contextMenu?.location?.organization_id ? (
          <MenuItem
            onClick={() =>
              openInNewTab(`/organizations/${contextMenu.location.organization_id}`)}
          >
            Open organization
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => openInNewTab(`/locations/${contextMenu?.location?.id}/providers`)}>
          Open providers
        </MenuItem>
        <MenuItem onClick={() => openInNewTab(`/locations/${contextMenu?.location?.id}/contacts`)}>
          Open contacts
        </MenuItem>
      </Menu>
    </>
  );
}

LocationsMapPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styled = withStyles(styles)(LocationsMapPage);
export { styled as LocationsMapPage };
